import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ICartActivityItem, ICartActivityItemGroup } from './interfaces';
import { ActivitiesCartListService } from './activities-cart-list.service';
import { IEventSettings, IGetActivitiesCartResponse } from 'src/app/shared/interfaces';
import { ApiService } from 'src/app/shared/api.service';
import { ActivityCartDisplayFilterItemId } from '../interfaces';
import { SharedService } from 'src/app/shared/shared.service';
import { ActivitiesCartDisplayType, IApplyDependenciesResult } from 'src/app/interfaces';
import { SubjectsService } from 'src/app/shared/subjects.service';
import { TRANSLATION_MAP } from 'src/app/enums';
import { CalendarType } from 'src/app/shared/enums';
import { ApiUtilsService } from 'src/app/shared/api-utils.service';

const initialActivityCartItemGroups = [];
const initialActivityCartItems = [];

@Component({
    selector: 'aza-rws-activities-cart-list',
    templateUrl: 'activities-cart-list.component.html',
    standalone: false
})
export class ActivitiesCartListComponent implements OnInit, OnDestroy, OnChanges {
    @Input() searchTextChanged: Subject<string>;
    @Input() eventSettings: IEventSettings;
    @Input() showActivitiesCart: Subject<IGetActivitiesCartResponse>;
    @Input() activitiesCartFilterSubject: ReplaySubject<ActivityCartDisplayFilterItemId>;
    @Input() activitiesCartFilterItemId: ActivityCartDisplayFilterItemId;
    @Input() displayType: ActivitiesCartDisplayType;
    // TODO: Provide this when we have header in the planner app builder
    @Input() displayTypeSelectFieldsGroupHeaderCssClass = '';
    @Input() locked = false;
    @Input() showAddToCalendar = false;
    @Input() hideAvailablePlacesLeft = false;

    @Output() activitiesChanged = new EventEmitter<ICartActivityItem[]>();

    quickSearchText = '';
    filteredActivityCartItems: ICartActivityItem[] = [];
    /** Please re-initiate `activityCartItemGroups` only with `initialActivityCartItemGroups` instead of `[]`,
    *
    * because `initialActivityCartItemGroups` is used to determine whether the property is initiated  */
    activityCartItemGroups: ICartActivityItemGroup[] = initialActivityCartItemGroups;
    pagingOffset = 0;
    pageSize = 5;
    timezone: string;
    dateTimeFormat: string;
    timeFormat: string;
    showAsSelectFields = false;
    selectedGroupActivityItem: ICartActivityItem;
    translations = TRANSLATION_MAP;
    calendarType = CalendarType;

    /** Please re-initiate `activityCartItems` only with `initialActivityCartItems` instead of `[]`,
     *
     * because `initialActivityCartItems` is used to determine whether the property is initiated  */
    private activityCartItems: ICartActivityItem[] = initialActivityCartItems;
    private getActivitiesCartResponse: IGetActivitiesCartResponse;
    private destroy$ = new Subject<void>();
    private applyDependencyResult: IApplyDependenciesResult | null = null;
    private registeredIn: string[] = [];

    constructor(
        private activitiesCartListSvc: ActivitiesCartListService, private apiSvc: ApiService,
        private sharedSvc: SharedService, private subjectsSvc: SubjectsService, private apiUtilSvc: ApiUtilsService
    ) {
    }

    async ngOnInit(): Promise<void> {
        this.timezone = this.eventSettings.timezone;
        this.dateTimeFormat = this.eventSettings.datetime_format;
        this.timeFormat = this.eventSettings.time_format;
        // this.searchTextChanged.pipe(takeUntil(this.destroy$)).subscribe({
        //     next: searchText => this.processSearchTextChanged(searchText)
        // });
        if (this.showActivitiesCart) {
            this.showActivitiesCart.pipe(takeUntil(this.destroy$)).subscribe({
                next: activitiesCartResponse => {
                    activitiesCartResponse && this.processShowActivitiesCart(activitiesCartResponse);
                }
            });
        }
        if (this.activitiesCartFilterSubject) {
            this.activitiesCartFilterSubject.pipe(takeUntil(this.destroy$)).subscribe({
                next: flags => this.processActivitiesCartDisplayFilterItemId(flags)
            });
        }
        this.subjectsSvc.getDependenciesAppliedObservable().pipe(takeUntil(this.destroy$)).subscribe({
            next: deps => this.processDependenciesApplied(deps)
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.displayType) {
            this.applyDisplayType(changes.displayType.currentValue);
        }
    }

    getActivityCartItemGroupIdentifier(index: number, grp: ICartActivityItemGroup): string {
        const cartActivity = grp.activityItems[0]?.cartActivity;
        if (!cartActivity) {
            return '';
        }
        return cartActivity.activity_group_id || cartActivity.id || '';
    }

    private processDependenciesApplied(deps: IApplyDependenciesResult): void {
        this.applyDependencyResult = deps;
        if (!this.applyDependencyResult) {
            return;
        }
        if (!this.getActivitiesCartResponse) {
            return;
        }
        if (this.showAsSelectFields) {
            this.registeredIn = this.activitiesCartListSvc.getRegisteredActivitiesFromLibraryGroupResult(
                this.activityCartItemGroups, this.applyDependencyResult?.dependencyResults, null
            );
        } else {
            this.registeredIn = this.activitiesCartListSvc.getRegisteredActivitiesFromLibraryListResult(
                this.activityCartItems, this.applyDependencyResult?.dependencyResults, null
            );
        }
        // TODO: Caching and reusing this.registeredIn can cause issues if it has "intermediate" result (meaning if evaluated with partial/empty dependencies which later change)
        this.getCart(this.registeredIn);
    }

    private applyDisplayType(displayType: ActivitiesCartDisplayType): void {
        this.showAsSelectFields = displayType === ActivitiesCartDisplayType.selectFields;
        if (
            (this.showAsSelectFields && this.activityCartItemGroups === initialActivityCartItemGroups) ||
            (!this.showAsSelectFields && this.activityCartItems === initialActivityCartItems)
        ) {
            this.getCart();
        }
    }

    private processActivitiesCartDisplayFilterItemId(itemId: ActivityCartDisplayFilterItemId): void {
        this.activitiesCartFilterItemId = itemId;
        this.filterActivityCartItems();
    }

    onGroupActivitySelected(group: ICartActivityItemGroup, ev: Event): void {
        const selectedActivityId = (ev.target as HTMLSelectElement).value;
        // Remove previous selection and add this one
        const previousSelection = this.activitiesCartListSvc.getRegisteredActivitiesFromLibraryGroupResult(
            this.activityCartItemGroups, this.applyDependencyResult?.dependencyResults, null
        );
        // Remove all selected activity ids which are in the selected group
        const groupSelection = this.activitiesCartListSvc.getRegisteredActivitiesFromLibraryGroupResult(
            [group], this.applyDependencyResult?.dependencyResults, null
        );
        this.registeredIn = previousSelection.filter(x => !groupSelection.includes(x));
        if (selectedActivityId && !this.registeredIn.includes(selectedActivityId)) {
            this.registeredIn.push(selectedActivityId);
        }
        this.activityCartItemGroups = this.activitiesCartListSvc.convertToCartActivityItemGroups(
            this.getActivitiesCartResponse, this.applyDependencyResult?.dependencyResults, this.registeredIn
        );
        const activityItems = [...this.activityCartItemGroups.flatMap(x => x.activityItems)];
        this.activitiesChanged.next(activityItems);
    }

    onAddToRegistration(activity: ICartActivityItem): void {
        const resActivity = this.getActivityFromCartActivityItem(activity);
        if (!resActivity) {
            return;
        }

        this.registeredIn = this.activitiesCartListSvc.getRegisteredActivitiesFromLibraryListResult(
            this.activityCartItems, this.applyDependencyResult?.dependencyResults, resActivity.id
        );
        this.activityCartItems = this.activitiesCartListSvc.convertToCartActivityItems(
            this.getActivitiesCartResponse, this.applyDependencyResult?.dependencyResults, this.registeredIn
        );
        this.filterActivityCartItems();
        this.activitiesChanged.next(this.activityCartItems);
    }

    onRemoveFromRegistration(activity: ICartActivityItem): void {
        const resActivity = this.getActivityFromCartActivityItem(activity);
        if (!resActivity) {
            return;
        }
        this.registeredIn = this.activitiesCartListSvc.getRegisteredActivitiesFromLibraryListResult(
            this.activityCartItems, this.applyDependencyResult?.dependencyResults, null
        ).filter(x => x !== resActivity.id);
        this.activityCartItems = this.activitiesCartListSvc.convertToCartActivityItems(
            this.getActivitiesCartResponse, this.applyDependencyResult?.dependencyResults, this.registeredIn
        );
        this.filterActivityCartItems();
        this.activitiesChanged.next(this.activityCartItems);
    }

    async onLikeActivity(activity: ICartActivityItem): Promise<void> {
        try {
            await this.apiSvc.likeActivity(activity.cartActivity.id);
            activity.cartActivity.like = true;
        } catch { }
    }

    async onUnlikeActivity(activity: ICartActivityItem): Promise<void> {
        try {
            await this.apiSvc.unlikeActivity(activity.cartActivity.id);
            activity.cartActivity.like = false;
        } catch { }
    }

    async openCalendar(activityId: string, calendarType: CalendarType) {
        const activity = this.getActivity(activityId);
        if (!activity) {
            return;
        }
        window.open(this.apiUtilSvc.getEventActivityCalendarGetUrl(activity.id, calendarType));
    }

    private processShowActivitiesCart(activitiesCartResponse: IGetActivitiesCartResponse): void {
        this.getActivitiesCartResponse = activitiesCartResponse;
        this.getCart();
    }

    // private async processSearchTextChanged(searchText: string): Promise<void> {
    //     this.pagingOffset = 0;
    //     this.quickSearchText = searchText;
    // }

    private getCart(registeredIn?: string[]): void {
        if (!this.getActivitiesCartResponse) {
            this.activityCartItems = initialActivityCartItems;
            this.activityCartItemGroups = initialActivityCartItemGroups;
            return;
        }
        this.registeredIn = registeredIn || this.activitiesCartListSvc.getRegisteredActivitiesFromServerResponse(
            this.getActivitiesCartResponse.activities, this.applyDependencyResult?.dependencyResults, null
        );
        if (this.showAsSelectFields) {
            this.activityCartItemGroups = this.activitiesCartListSvc.convertToCartActivityItemGroups(
                this.getActivitiesCartResponse, this.applyDependencyResult?.dependencyResults, this.registeredIn
            );
            const activityItems = [...this.activityCartItemGroups.flatMap(x => x.activityItems)];
            this.activitiesChanged.next(activityItems);
        } else {
            this.activityCartItems = this.activitiesCartListSvc.convertToCartActivityItems(
                this.getActivitiesCartResponse, this.applyDependencyResult?.dependencyResults, this.registeredIn
            );
            this.filterActivityCartItems();
            this.activitiesChanged.next(this.activityCartItems);
        }
    }

    private filterActivityCartItems(): void {
        let filtered: ICartActivityItem[] = [];
        if (this.activitiesCartFilterItemId === ActivityCartDisplayFilterItemId.all) {
            filtered.push(...this.activityCartItems);
        } else if (this.activitiesCartFilterItemId === ActivityCartDisplayFilterItemId.liked) {
            filtered.push(...this.activityCartItems.filter(x => x.cartActivity.like));
        } else if (this.activitiesCartFilterItemId === ActivityCartDisplayFilterItemId.motLiked) {
            filtered.push(...this.activityCartItems.filter(x => !x.cartActivity.like));
        } else if (this.activitiesCartFilterItemId === ActivityCartDisplayFilterItemId.registered) {
            filtered.push(...this.activityCartItems.filter(x => x.selected));
        } else if (this.activitiesCartFilterItemId === ActivityCartDisplayFilterItemId.notRegistered) {
            filtered.push(...this.activityCartItems.filter(x => !x.selected));
        }

        // Exclude activities which are disabled by dependency
        filtered = filtered.filter(x => !x.disabledByDependency);
        // TODO: The server returns string in the start and end attributes
        //       but the interface declares them as Date type
        //       We do not use Date time in our front-end applications
        this.sharedSvc.sortByString(filtered, x => (x.cartActivity.start as any as string));
        this.filteredActivityCartItems = filtered;
    }

    private getActivityFromCartActivityItem(activity: Pick<ICartActivityItem, 'cartActivity'>) {
        const activityId = activity.cartActivity.id;
        return this.getActivity(activityId);
    }

    private getActivity(activityId: string) {
        return this.getActivitiesCartResponse.activities.find(x => x.id === activityId);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
